/**
 * Mirrors the server's contracts exactly (2.7, 2.8). This file has no
 * server-side dependency — it's the client's own copy of the wire shapes,
 * kept in sync by convention (same envelope, same error-code strings) since
 * client/ isn't part of the packages/shared-* npm workspace boundary.
 */

// ---- REST (2.7) --------------------------------------------------------

export interface ApiSuccessBody<T> {
  data: T;
  meta: { requestId: string; nextCursor?: string | null };
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    requestId: string;
    // Present for VALIDATION_ERROR responses — server (e.g. auth-worker's
    // register route) attaches Zod's .flatten() output here, giving
    // per-field reasons. Previously parsed off but never plumbed through
    // to callers, so a UI could only ever show the generic top-level
    // message ("Invalid registration payload") with no indication of
    // which field or why — discovered when that generic message alone
    // wasn't enough to diagnose a real registration failure.
    details?: unknown;
  };
}

export type ApiResult<T> =
  | { ok: true; data: T; meta: ApiSuccessBody<T>["meta"] }
  | { ok: false; code: string; message: string; requestId?: string; httpStatus: number; details?: unknown };

// ---- Real-time (2.8) ----------------------------------------------------

export type ClientMessageType = "move" | "interact" | "use_skill" | "heartbeat" | "chat_message";
export type ServerMessageType = "state_sync" | "entity_update" | "pong" | "chat_message";

export interface ClientMessage<TPayload = unknown> {
  type: ClientMessageType;
  payload: TPayload;
}

export interface ServerMessage<TPayload = unknown> {
  type: ServerMessageType;
  timestamp: number;
  payload: TPayload;
}

export const HEARTBEAT_INTERVAL_MS = 4000;
